"use client";

import Script from "next/script";

import { markUtagLoaded } from "@repo/ui/lib/utagReady";

declare global {
  interface Window {
    utag?: {
      view: (data: Record<string, unknown>) => void;
      link: (data: Record<string, unknown>) => void;
    };
    utag_data?: Record<string, unknown>;
    utag_cfg_ovrd?: Record<string, unknown>;
  }
}

export default function TealiumLoader({ utagScriptUrl }: Readonly<{ utagScriptUrl: string }>) {
  return (
    <>
      <Script
        id="tealium-data-layer-init"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          // utag.js reads utag_data and utag_cfg_ovrd at load time, so both must be
          // set before it executes — which beforeInteractive guarantees.
          // `noview` suppresses utag.js's implicit page view so the app owns every
          // view event (see useTealiumPageView).
          __html: `
            window.utag_cfg_ovrd = window.utag_cfg_ovrd || {};
            window.utag_cfg_ovrd.noview = true;

            window.utag_data = ${serializeForScript(UTAG_DATA)};
          `,
        }}
      />
      <Script
        strategy="afterInteractive"
        src={utagScriptUrl}
        onLoad={() => {
          markUtagLoaded(true);
          if (process.env.NODE_ENV === "development") {
            console.log("[Analytics] Tealium loaded");
          }
        }}
        onError={() => {
          markUtagLoaded(false);
          if (process.env.NODE_ENV === "development") {
            console.warn("[Analytics] Tealium failed to load");
          }
        }}
      />
    </>
  );
}

/** Escape `<` so a value can never terminate the inline <script> block early. */
function serializeForScript(value: Record<string, string>): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

const UTAG_DATA: Record<string, string> = {
  fb_pid: "",
  PNR: "",
  accept_language: "",
  additional_cookie: "",
  breakdown_ancillaries_amount: "",
  breakdown_currency: "",
  breakdown_due_amount: "",
  breakdown_fare_amount: "",
  breakdown_tax_amount: "",
  breakdown_total_amount: "",
  browser: "",
  browser_version: "",
  checkin_status_inbound: "",
  checkin_status_outbound: "",
  city_code: "",
  collection_currency: "",
  collection_total: "",
  corporate_release: "",
  country_code: "",
  culture: "",
  dclub_group_currency: "",
  dclub_group_price: "",
  dclub_standard_currency: "",
  dclub_standard_price: "",
  destination_selector_values: "",
  device_brand: "",
  device_model: "",
  device_type: "", // D - Desktop,T - tablet,M - mobile
  error_code: "", // 500,422,404,...
  error_info: "", // Detail of error
  flow_type: "", // DB - During Booking,PB - Post Booking,CK - Checkin
  fop_brand: "",
  fop_code: "",
  ibe_release: "",
  inbound_arrival_iata: "",
  inbound_availability_days: "", // 3 month days availability (calendar)
  inbound_departure_iata: "",
  inbound_fare_price: "",
  inbound_fare_currency: "",
  inbound_flights_availability: "", // Available inbound flights: inventory type,flight number,departure date,arrival date,regular flight price,regular flight currency,dclub flight price,dclub flight currency. Separated by pipe
  inbound_seat_availability: "",
  inbound_seat_selected: "", // Seat's code,Seat's price,Seat's currency. Separated by pipe
  inbound_selected_flight: "", // Selected inbound flight: inventory type,flight number,departure date,arrival date,flight price,flight currency. Separated by pipe
  installment_availability: "", // True/False if either is enabled for that FoP
  installment_options_availability: "", // Options available for current FoP selected
  installment_selected: "",
  interest_currency: "",
  interest_fee: "", // Interest fee for current installment option selected
  interest_rate: "", // Interest rate for current installment option selected
  last_payment_amount: "", // Last amount paid (not the total of flight)
  last_payment_status: "", // Last status of payment Approved,Rejected,Pending
  market_code: "",
  organization: "", // Organization where user belongs
  outbound_arrival_iata: "",
  outbound_availability_days: "", // 3 month days availability (calendar)
  outbound_departure_iata: "",
  outbound_fare_price: "",
  outbound_fare_currency: "",
  outbound_flights_availability: "", // Available inbound flights: inventory type,flight number,departure date,arrival date,regular flight price,regular flight currency,dclub flight price,dclub flight currency. Separated by pipe
  outbound_seat_availability: "",
  outbound_seat_selected: "", // Seat's code,Seat's price,Seat's currency. Separated by pipe
  outbound_selected_flight: "", // Selected outbound flight: inventory type,flight number,departure date,arrival date,flight price,flight currency. Separated by pipe
  page: "",
  page_version: "",
  pax_adult_count: "",
  pax_children_count: "",
  pax_infant_count: "",
  preloaded_promocode: "",
  price: "",
  price_currency: "",
  price_info: "",
  program: "",
  promocode: "",
  referer: "",
  referral: "",
  session_create_date: "",
  sessionid: "",
  ssrs_code_available_inbound: "", // These are the SSRs available for add-to-cart in a particular page. In baggage page,it would be all baggage SSR available for purchase
  ssrs_code_available_outbound: "", //
  ssrs_code_included_inbound: "", // SSR's code,SSR's price,SSR's currency. Separated by pipe
  ssrs_code_included_outbound: "", // SSR's code,SSR's price,SSR's currency. Separated by pipe
  standard_price: "",
  standard_price_currency: "",
  tealium_event: "", // Tealium variable to identify unique events and page types eg. home,product_view,cart_add,user_login,email_signup,etc.
  user_programs: "",
  user_role: "",
  useragent: "",
  voucher_amount: "",
  voucher_amount_used: "", // Amount used of the voucher or giftcard
  voucher_code: "",
  voucher_currency: "",
  pax_set_array: "",
  page_name: "", // Contains a user-friendly name for the page.
  page_section: "", // Contains a user-friendly page section, e.g. 'configuration section'.
  page_category: "", // Contains a user-friendly page category, e.g. 'appliance page'.
  page_subcategory: "", // Contains a user-friendly page subcategory, e.g. 'refrigerator page'.
  page_type: "", // Contains a user-friendly page type, e.g. 'cart page'.
  customer_id: "", // Contains the customer's ID.
  customer_username: "", // Contains the customer's username, e.g. johndoe38.
  customer_status: "", // Contains the customer's status, e.g. 'logged in'.
  customer_type: "", // Contains the customer's type, e.g. 'bronze member'.
  customer_email: "", // Contains the customer's email.
  customer_city: "", // Contains the city portion of the customer's address.
  customer_state: "", // Contains the state portion of the customer's address.
  customer_zip: "", // Contains the zip code portion of the customer's address.
  customer_country: "", // Contains the country portion of the customer's address.
  search_term: "", // Contains the search term or query submitted by a visitor.
  search_results: "", // Contains the number of results returned with a search query.
  search_type: "", // Contains the type of search conducted by the visitor.
  product_id: "", // Contains product ID(s) - multiple values should be comma-separated.
  product_sku: "", // Contains product SKU(s) - multiple values should be comma-separated.
  product_name: "", // Contains product name(s) - multiple values should be comma-separated.
  product_brand: "", // Contains product brand(s) - multiple values should be comma-separated.
  product_category: "", // Contains product category(s) - multiple values should be comma-separated.
  product_subcategory: "", // Contains product subcategory(s) - multiple values should be comma-separated.
  product_unit_price: "", // Contains product unit price(s) - multiple values should be comma-separated.
  product_list_price: "", // Contains product list price(s) before discount - multiple values should be comma-separated.
  product_quantity: "", // Contains product quantity(s) - multiple values should be comma-separated.
  product_discount: "", // Contains product discount(s) - multiple values should be comma-separated.
  order_id: "", // Contains the order or transaction ID.
  order_total: "", // Contains the order totanl value.
  order_subtotal: "", // Contains the order subtotal (not containing taxes & shipping).
  a: "", // Contains the order payment type.
  order_discount: "", // Contains the order discount (if any).
  order_shipping: "", // Contains the order shipping value.
  order_tax: "", // Contains the order tax amount.
  order_currency: "", // Contains the currency associated with the transaction, e.g. 'USD'.
  order_coupon_code: "", // Contains the order coupon code.
  order_coupon_discount: "", // Contains the order coupon discount.
  order_store: "", // Contains identification information for a store.
  order_type: "", // Contains type of order/cart.
  site_region: "", // Contains values for the region or localized version, e.g. 'en_US'.
  article_id: "", // Contains the article's ID.
  article_name: "", // Contains the article's name.
  article_author: "", // Contains the name of the author of an article.
  article_publish_date: "", // Contains the publish date of an article.
  order_number: "",
  payment_id: "",
  number_of_installments: "",
  authorization_code: "",
  transaction_date: "",
  last_4_digits: "",
  amount: "",
  curency_code: "",
  quoted_currency: "",
  quoted_total: "",
  availability: "",
  inbound_availability: "",
  outbound_availability: "",
  auto_opened: "",
  currency: "",
  currency_code: "",
  gads_id: "",
  gads_label: "",
  ampl_event_name: "",
  station_code: "",
  trip_type: "",
  availability_day: "",
  date: "",
  fare: "",
  baggage_availability_outbound: "",
  baggage_included_outbound: "",
  baggage_availability_inbound: "",
  baggage_included_inbound: "",
  pax_index: "",
  carrier_code: "",
  airline: "",
  flight_number: "",
  departure: "",
  arrival: "",
  origin: "",
  destination: "",
  dclub_price: "",
  dclub_price_currency: "",
  fare_type: "",
  fare_info: "",
  departure_iata: "",
  pax_type: "",
  direction: "",
  inbound_direction: "",
  outbound_direction: "",
  quantity: "",
  previous_quantity: "",
  outbound_inventory_type: "",
  inbound_inventory_type: "",
  seat_number: "",
  previous_seat_number: "",
  previous_price: "",
  previous_currency: "",
  previous_type: "",
  previous_direction: "",
  previous_amount: "",
  card_mask: "",
  amount_due_total: "",
  amount_due_currency: "",
  leg: "",
  third_party_type: "",
  role: "",
  type: "",
  departure_date: "",
  outbound_departure_date: "",
  outbound_arrival_date: "",
  outbound_price_info: "",
  inbound_price_info: "",
  inbound_arrival_date: "",
  messages: "",
  transaction_date_utc: "",
  transaction_id: "",
  resolution: "",
  load_type: "",
  fop_code_availability: "",
  inbound_departure_date: "",
  tealium_visitor_id: "",
  route: "",
  outbound_fare_departure_date: "",
  inbound_fare_departure_date: "",
  outbound_fare_departure_iata: "",
  outbound_fare_arrival_iata: "",
  outbound_fare_arrival_date: "",
  inbound_fare_arrival_date: "",
  event_name: "", // Tealium variable to identify unique events eg. social_share, user_login, etc.
  fb_flight_click: "", // flight page continue click
  ampl_id: "",
  inbound_fare_departure_iata: "",
  inbound_fare_arrival_iata: "",
  origin_iata: "",
  destination_iata: "",
  user_dc_level: "",
  membership_type: "",
  inbound_route: "",
  device: "",
  total_ancillary: "",
  total_tax: "",
  outbound_bundle: "",
  inbound_bundle: "",
  contact_email: "",
  soicos_pid: "",
  ga_action: "",
  ga_category: "",
  utm_value: "",
  is_gds_booking: "", // Boolean for type gds
  inbound_fare_currency_code: "",
  outbound_fare_currency_code: "",
  route_outbound_fs: "",
  supplier: "",
  flights_availability: "",
  iq_view_id: "", // A system variable that contains the ID of an iQ page event and used to fire a tag when a page view event happens.
  iq_event_id: "", // A system variable that contains the ID of an iQ event and used to fire a tag when an event happens.
  curreny_selected: "",
  booking_total_amount: "",
  booking_currency: "",
  booking_total_ancillaries: "",
  booking_channel_type: "",
  cabing_bag: "",
  checked_bag: "",
  flexismart: "",
  q_pax: "",
  departure_journey_date: "",
  return_journey_date: "",
  booking_total_tax: "",
  fb_contact_email: "",
  contact_email_hash: "",
  adult: "",
  arrival_date: "",
  seats: "",
  oversize_baggage: "",
  total_fare: "",
  booking_total_amount_usd: "",
  booking_total_ancillaries_usd: "",
  outbound_fare_price_usd: "",
  inbound_fare_price_usd: "",
  total_transaction_amount: "",
  fbclid_variable_temp: "",
  total_transaction_amount_usd: "",
  countBaggage: "",
  amountBaggage: "",
  countLBGP: "",
  amountLBGP: "",
  customer_name: "",
  route_inbound_fs: "",
  landing: "",
  cabin_bag_count: "",
  checked_bag_count: "",
  bundle_smart_count: "",
  bundle_full_count: "",
  market: "",
  insurance: "",
  fare_class: "",
  is_connected: "",
  user_type: "",
  button_id: "",
  url: "",
  outbound_journeys: "",
  return_journeys: "",
  position: "",
  numAA: "",
  with_taxes: "",
  question: "",
  rtgroup: "",
  has_cabin_bag: "",
  has_checked_bag: "",
  bundle_code: "",
  state: "",
  baggageAmountUSD: "",
  error_cabin_bag: "",
  error_checked_bag: "",
  error_flight_not_selected: "",
  error_DC_not_selected: "",
  outbound_journeys_quantity: "",
  return_journeys_quantity: "",
  pathname: "",
  bundle_type: "",
  seats_count: "",
  pnr: "",
  journey_type: "",
  culture_home: "",
  role_code: "",
  user_be_level: "",
  program_code: "",
  program_level: "",
  filter: "",
  is_in_outbound: "",
  is_in_inbound: "",
  direct_outbound: "",
  direct_inbound: "",
  cnx_outbound: "",
  cnx_inbound: "",
  count: "",
  baggage_type: "",
  payment_date: "",
  payment_date_utc: "",
  payment_date_chile: "",
  booking_data_b64: "",
  total_passengers_adults: "",
  total_femenine: "",
  total_masculine: "",
  passengers_total_count: "",
  pax_toggle_status: "",
  pax_action_type: "",
  click_header_button: "",
  click_searchbox_header: "",
  click_third_parties: "",
  click_checkbox_miles_aa: "",
  click_destinations_card: "",
  click_taxes_toggle: "",
  click_see_all_destinations: "",
  click_widgets_home: "",
  searches_count: "",
  width_screen: "",
  height_screen: "",
  // Set on every page view by useTealiumPageView.
  currentPathname: "",
  currentCulture: "",
};
